import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export interface WebhookEndpoint {
  id: string;
  tenant_id: string;
  name: string;
  url: string;
  event_types: string[];
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface WebhookEvent {
  id: string;
  endpoint_id: string;
  tenant_id: string;
  event_type: string;
  status: 'success' | 'failed' | 'pending';
  attempts: number;
  last_attempt_at: string | null;
  response_status: number | null;
  error_message: string | null;
  endpoint_name: string;
  created_at: string;
}

export interface WebhookEventStats {
  success: number;
  failed: number;
  pending: number;
  total: number;
}

export type WebhookEventType =
  | 'deliverable.status_changed'
  | 'deliverable.created'
  | 'invoice.sent'
  | 'invoice.paid';

export const WEBHOOK_EVENT_TYPES: { value: WebhookEventType; label: string }[] = [
  { value: 'deliverable.status_changed', label: 'Deliverable Status Changed' },
  { value: 'deliverable.created', label: 'Deliverable Created' },
  { value: 'invoice.sent', label: 'Invoice Sent' },
  { value: 'invoice.paid', label: 'Invoice Paid' },
];

// ============================================================================
// Query Hooks
// ============================================================================

export function useWebhookEndpoints(includeInactive = false) {
  return useQuery({
    queryKey: ['webhook-endpoints', includeInactive],
    queryFn: async () => {
      const response = await api.webhooks.listEndpoints(includeInactive);
      return response.endpoints as WebhookEndpoint[];
    },
  });
}

export function useWebhookEvents(endpointId?: string, limit = 50) {
  return useQuery({
    queryKey: ['webhook-events', endpointId, limit],
    queryFn: async () => {
      const response = await api.webhooks.listEvents(endpointId, limit);
      return response.events as WebhookEvent[];
    },
  });
}

export function useWebhookEventStats() {
  return useQuery({
    queryKey: ['webhook-events', 'stats'],
    queryFn: async () => {
      const response = await api.webhooks.eventStats();
      return response.stats as WebhookEventStats;
    },
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateWebhookEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; url: string; event_types: string[] }) =>
      api.webhooks.createEndpoint(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      toast.success('Webhook endpoint created');
      return result;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create webhook endpoint');
    },
  });
}

export function useUpdateWebhookEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      api.webhooks.updateEndpoint(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      toast.success('Webhook endpoint updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update webhook endpoint');
    },
  });
}

export function useDeleteWebhookEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.webhooks.deleteEndpoint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      toast.success('Webhook endpoint deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete webhook endpoint');
    },
  });
}

export function useTestWebhookEndpoint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.webhooks.testEndpoint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-events'] });
      toast.success('Test event sent — check your notification channel');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send test event');
    },
  });
}

// ============================================================================
// Slack Bot Hooks (Phase 10c.10)
// ============================================================================

export function useValidateSlackToken() {
  return useMutation({
    mutationFn: (botToken: string) =>
      api.webhooks.validateSlackToken(botToken),
    onError: (error: any) => {
      toast.error(error.message || 'Failed to validate Slack token');
    },
  });
}

export function useSetupSlackBot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; bot_token: string; channel_id: string; event_types: string[]; channel_mode: string }) =>
      api.webhooks.setupSlackBot(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      toast.success(result.message || 'Slack Bot connected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to set up Slack Bot');
    },
  });
}
